import React, { useEffect } from 'react';

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Federal IP Office - Legal Services';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Privacy Policy for Federal IP Office. Learn how we collect, use, and protect your personal information.');
    }
  }, []);

  return (
    <div className="pt-20" data-testid="privacy-policy-page">
      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-[#2C3E50] font-bold mb-6">
            Privacy Policy
          </h1>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Introduction</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              Federal IP Office ("we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Information We Collect</h2>
            <h3 className="text-xl font-semibold text-[#2C3E50] mb-3">Personal Information</h3>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-4">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc list-inside space-y-2 text-base text-[#2E2E2E]/80 ml-4">
              <li>Contact us through our website forms</li>
              <li>Request a consultation or legal services</li>
              <li>Subscribe to our newsletter or communications</li>
              <li>Engage us for legal representation</li>
            </ul>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 mt-4">
              This information may include your name, email address, phone number, company name, and details about your legal needs.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-[#2C3E50] mb-3">Automatically Collected Information</h3>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              When you visit our website, we may automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies installed on your device. We may also collect information about your browsing actions and patterns.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">How We Use Your Information</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-base text-[#2E2E2E]/80 ml-4">
              <li>Provide, operate, and maintain our legal services</li>
              <li>Respond to your inquiries and communicate with you</li>
              <li>Send you updates, newsletters, and marketing communications (with your consent)</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations and protect our legal rights</li>
              <li>Prevent fraud and enhance security</li>
            </ul>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Information Sharing and Disclosure</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
            </p>
            <ul className="list-disc list-inside space-y-2 text-base text-[#2E2E2E]/80 ml-4">
              <li>With service providers who assist us in operating our website and providing services</li>
              <li>When required by law or to protect our rights</li>
              <li>In connection with a business transaction, such as a merger or sale of assets</li>
              <li>With your consent or at your direction</li>
            </ul>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Attorney-Client Privilege</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              Information shared with us in the context of legal representation is protected by attorney-client privilege and will be kept strictly confidential in accordance with professional ethical rules and applicable law.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Data Security</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Your Rights</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-4">
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-base text-[#2E2E2E]/80 ml-4">
              <li>The right to access and receive a copy of your personal information</li>
              <li>The right to correct inaccurate information</li>
              <li>The right to request deletion of your information</li>
              <li>The right to object to or restrict processing</li>
              <li>The right to data portability</li>
            </ul>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Cookies and Tracking Technologies</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              We use cookies and similar tracking technologies to track activity on our website and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Children's Privacy</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Changes to This Privacy Policy</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-[#2C3E50] mb-4">Contact Us</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80">
              If you have questions about this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <div className="mt-4 p-6 bg-[#F8F9FA] border border-[#2C3E50]/10">
              <p className="font-semibold text-[#2C3E50]">Federal IP Office</p>
              <p className="text-[#2E2E2E]/80">550 W B St, San Diego, CA 92101</p>
              <p className="text-[#2E2E2E]/80">Email: Info@federalipoffice.com</p>
              <p className="text-[#2E2E2E]/80">Phone: <a href="tel:+18335187356" className="hover:text-[#2C3E50]">+1 (833) 518-7356 </a></p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
